import React, { Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import PageViewTracker from './components/PageViewTracker';
import ProtectedRoute from './components/ProtectedRoute';
import RequireProjectPermission from './components/RequireProjectPermission';
import AdminProtectedRoute from './components/AdminProtectedRoute';
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import AuthCallback from './pages/AuthCallback';
import SignupPage from './pages/SignupPage';
import VerifyEmail from './pages/VerifyEmail';
import PricingPage from './pages/PricingPage';
import BillingPage from './pages/BillingPage';
import CheckoutPage from './pages/CheckoutPage';
import ProfilePage from './pages/ProfilePage';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import LinkInvite from './pages/LinkInvite';
import ShareInvite from './pages/ShareInvite';
import Dashboard from './pages/Dashboard';
import PublishDashboard from './pages/PublishDashboard';
import ProjectList from './pages/ProjectList';
import ProjectDetail from './pages/ProjectDetail';
import VideoEditor from './pages/VideoEditor';
import ItemViewer from './pages/ItemViewer';
import YRay from './pages/YRay';
import YRayItem from './pages/YRayItem';
import ClipList from './pages/ClipList';
import ClipEditor from './pages/ClipEditor';
import ImageVideoUpload from './pages/ImageVideoUpload';
import KineticCaption from './pages/KineticCaption';
import SocialAccounts from './pages/SocialAccounts';
import Posts from './pages/Posts';
import PostsCompose from './pages/PostsCompose';
import PostsDrafts from './pages/PostsDrafts';
import PostsLive from './pages/PostsLive';
import VideoInsights from './pages/VideoInsights';
import PostsArchived from './pages/PostsArchived';
import PostsScheduled from './pages/PostsScheduled';
import AnalyticsDashboard from './pages/AnalyticsDashboard';
import AdminPricingPayg from './pages/AdminPricingPayg';
import AdminPricingPlans from './pages/AdminPricingPlans';
import AdminDefaults from './pages/AdminDefaults';
import AdminUsers from './pages/AdminUsers';
import AdminUserDetail from './pages/AdminUserDetail';
import AdminAssets from './pages/AdminAssets';
import AdminSpotWorkers from './pages/AdminSpotWorkers';
import AdminWaitlist from './pages/AdminWaitlist';

// The mailer admin module (specs/mailer/mail.md, 13.2) ships as its own chunk.
const MailerApp = React.lazy(() => import('./mailer/MailerApp'));

const App: React.FC = () => (
  <BrowserRouter>
    <PageViewTracker />
    <ThemeProvider>
    <AuthProvider>
      <Routes>
        {/* Public routes */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/login/verify/:token" element={<VerifyEmail />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/link-invite/:token" element={<LinkInvite />} />
        <Route path="/share-invite/:token" element={<ShareInvite />} />

        {/* Protected routes */}
        <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/choose-plan" element={<ProtectedRoute><Dashboard choosePlan /></ProtectedRoute>} />
        <Route path="/checkout" element={<ProtectedRoute><CheckoutPage /></ProtectedRoute>} />
        <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
        <Route path="/account" element={<ProtectedRoute><SocialAccounts /></ProtectedRoute>} />
        <Route path="/account/billing" element={<ProtectedRoute><BillingPage /></ProtectedRoute>} />
        <Route path="/analytics" element={<ProtectedRoute><AnalyticsDashboard /></ProtectedRoute>} />
        <Route path="/analytics/insights" element={<ProtectedRoute><PostsLive /></ProtectedRoute>} />
        <Route path="/analytics/insights/:postId" element={<ProtectedRoute><VideoInsights /></ProtectedRoute>} />

        {/* Posts routes */}
        <Route path="/posts" element={<ProtectedRoute><Posts /></ProtectedRoute>} />
        <Route path="/posts/new" element={<ProtectedRoute><Navigate to="/posts/compose" replace /></ProtectedRoute>} />
        <Route path="/posts/compose" element={<ProtectedRoute><PostsCompose /></ProtectedRoute>} />
        <Route path="/posts/drafts" element={<ProtectedRoute><PostsDrafts /></ProtectedRoute>} />
        <Route path="/posts/live" element={<ProtectedRoute><PostsLive /></ProtectedRoute>} />
        <Route path="/posts/live/:postId/insights" element={<ProtectedRoute><VideoInsights /></ProtectedRoute>} />
        <Route path="/posts/archived" element={<ProtectedRoute><PostsArchived /></ProtectedRoute>} />
        <Route path="/posts/scheduled" element={<ProtectedRoute><PostsScheduled /></ProtectedRoute>} />

        {/* Project routes */}
        <Route path="/projects" element={<ProtectedRoute><ProjectList /></ProtectedRoute>} />
        <Route path="/projects/:id" element={<ProtectedRoute><ProjectDetail /></ProtectedRoute>} />
        <Route path="/projects/:id/y-ray" element={<ProtectedRoute><YRay /></ProtectedRoute>} />
        {/* Declared before the generic item route; a static segment outranks :kind anyway. */}
        <Route path="/projects/:projectId/item/y-ray/:videoId" element={<ProtectedRoute><YRayItem /></ProtectedRoute>} />
        <Route path="/projects/:id/image-video" element={<ProtectedRoute><RequireProjectPermission permission="can_edit_story" param="id"><ImageVideoUpload /></RequireProjectPermission></ProtectedRoute>} />
        <Route path="/projects/:id/image-video/:storyId" element={<ProtectedRoute><RequireProjectPermission permission="can_edit_story" param="id"><ImageVideoUpload /></RequireProjectPermission></ProtectedRoute>} />
        <Route path="/projects/:id/kinetic-caption" element={<ProtectedRoute><RequireProjectPermission permission="can_edit_caption" param="id"><KineticCaption /></RequireProjectPermission></ProtectedRoute>} />
        <Route path="/projects/:id/kinetic-caption/:workspaceId" element={<ProtectedRoute><RequireProjectPermission permission="can_edit_caption" param="id"><KineticCaption /></RequireProjectPermission></ProtectedRoute>} />
        {/* Read-only landing page for anything a project holds — its renders and a player. */}
        <Route path="/projects/:projectId/item/:kind/:itemId" element={<ProtectedRoute><ItemViewer /></ProtectedRoute>} />
        <Route path="/projects/:projectId/video/:videoId" element={<ProtectedRoute><RequireProjectPermission permission="can_edit_clip" param="projectId"><VideoEditor /></RequireProjectPermission></ProtectedRoute>} />
        <Route path="/projects/:projectId/video/:videoId/publish" element={<ProtectedRoute><RequireProjectPermission permission="can_publish" param="projectId"><PublishDashboard /></RequireProjectPermission></ProtectedRoute>} />

        {/* Clip editor routes */}
        <Route path="/clips" element={<ProtectedRoute><ClipList /></ProtectedRoute>} />
        <Route path="/clips/:shortId" element={<ProtectedRoute><ClipEditor /></ProtectedRoute>} />
        <Route path="/projects/:projectId/video/:videoId/clip/:shortId" element={<ProtectedRoute><RequireProjectPermission permission="can_edit_clip" param="projectId"><ClipEditor /></RequireProjectPermission></ProtectedRoute>} />

        {/* Admin routes */}
        <Route path="/admin" element={<AdminProtectedRoute><Navigate to="/admin/users" replace /></AdminProtectedRoute>} />
        <Route path="/admin/pricing/payg" element={<AdminProtectedRoute><AdminPricingPayg /></AdminProtectedRoute>} />
        <Route path="/admin/pricing/plans" element={<AdminProtectedRoute><AdminPricingPlans /></AdminProtectedRoute>} />
        <Route path="/admin/defaults" element={<AdminProtectedRoute><AdminDefaults /></AdminProtectedRoute>} />
        <Route path="/admin/users" element={<AdminProtectedRoute><AdminUsers /></AdminProtectedRoute>} />
        <Route path="/admin/users/:userId" element={<AdminProtectedRoute><AdminUserDetail /></AdminProtectedRoute>} />
        <Route path="/admin/assets" element={<AdminProtectedRoute><AdminAssets /></AdminProtectedRoute>} />
        <Route path="/admin/spot-workers" element={<AdminProtectedRoute><AdminSpotWorkers /></AdminProtectedRoute>} />
        <Route path="/admin/waitlist" element={<AdminProtectedRoute><AdminWaitlist /></AdminProtectedRoute>} />
        <Route path="/admin/mailer/*" element={<AdminProtectedRoute><Suspense fallback={<div className="admin-loading">Loading…</div>}><MailerApp /></Suspense></AdminProtectedRoute>} />
      </Routes>
    </AuthProvider>
    </ThemeProvider>
  </BrowserRouter>
);

export default App;
